import React from 'react';
import { useDispatch } from 'react-redux';
import {
  Button,
  Icon,
  IconButton,
  FormHelperText
} from '@material-ui/core';
import { BackdropCircular } from 'components';
import { assetManagerUrl } from 'configs';
import {
  uploadFileTypeUnsupported,
  uploadFileSizeLimit
} from 'redux/actions/general';
import { uploadPrivateImageApi, getAssetManagerTagByCodeApi } from 'api';
import GallerySelectOwnImage from './GallerySelectOwnImage';
import useGalleryAssetTags from './useGalleryAssetTags';
import {
  GALLERY_ASSET_TAG_CODE,
  GALLERY_ASSET_TAG_LABEL
} from './constants';


interface IGalleryImagePickerProps {
  id: string,
  dialogTitle: string,
  onSelect: (_imageUrl: string) => void,
  value?: string,
  showClearButton?: boolean,
  allowPng?: boolean,
  recommendedSize?: string,
  assetTagCode?: string,
  assetTagLabel?: string,
  rowHeight?: number
}

const GalleryImagePicker = ({
  id,
  dialogTitle,
  onSelect,
  value = '',
  showClearButton = false,
  allowPng = true,
  recommendedSize = '',
  assetTagCode = GALLERY_ASSET_TAG_CODE,
  assetTagLabel = GALLERY_ASSET_TAG_LABEL,
  rowHeight = 150
}: IGalleryImagePickerProps) => {
  const dispatch = useDispatch();

  const {
    assetTag,
    privateTag,
    storeCodeTag,
    ownImagesTagIds,
    currentCategoryId,
    storeCode,
    assetImagesData,
    uploadingPrivateImage
  } = useGalleryAssetTags(assetTagCode);

  const handleSelectImage = (data) => {
    onSelect(`${assetManagerUrl}/${data.webPath}`);
  };

  const handleUpLoadImage = async (event) => {
    const {files, value: filePath} = event.target;

    if (!files.length) {
      return;
    }

    const [file] = files;

    const mimePattern = allowPng ? /^image\/(png|jpeg)$/i : /^image\/jpeg$/i;
    const extensionPattern = allowPng ? /\.(png|jpe?g)$/i : /\.jpe?g$/i;

    if (!(mimePattern.test(file.type) || extensionPattern.test(filePath))) {
      dispatch(uploadFileTypeUnsupported());
      return;
    }

    if (file.size > 256000) {
      dispatch(uploadFileSizeLimit('250kB'));
      return;
    }

    const formData = new FormData();
    formData.append('image', file);

    const isAddingNewTag = !ownImagesTagIds.length;

    if (isAddingNewTag) {
      // A new-tag object in the tags JSON fails the whole upload with "Tag code already exists"
      // if the tag was created outside this page, so when a tag is missing from the (possibly
      // stale) category tag list, re-check it by code upstream before asking to create it.
      const resolveUploadTag = async (localTag, tagCode, label) => {
        if (localTag) {
          return localTag.tagCode;
        }

        const existingTag = await getAssetManagerTagByCodeApi({dispatch, tagCode});

        return existingTag ? existingTag.tagCode : {label, tagCode, category: currentCategoryId};
      };

      const tags = [
        await resolveUploadTag(assetTag, assetTagCode, assetTagLabel),
        privateTag ? privateTag.tagCode : 'private',
        await resolveUploadTag(storeCodeTag, storeCode, storeCode)
      ];
      formData.append('tags', JSON.stringify(tags));
    } else {
      formData.append('tags', `${assetTagCode},private,${storeCode}`);
    }

    const imageData = await uploadPrivateImageApi({dispatch, formData, isAddingNewTag});

    if (imageData) {
      handleSelectImage(imageData);
    }
  };

  return (
    <div>
      {value &&
        <div className="flex items-center mb-2">
          <img src={value} alt={dialogTitle} className="max-w-300 max-h-100" />
          {showClearButton &&
            <IconButton size="small" className="ml-2" title="Remove image" onClick={() => onSelect('')}>
              <Icon>delete</Icon>
            </IconButton>
          }
        </div>
      }
      <div>
        <Button
          className="p-0 w-full max-w-300 text-center"
          variant="outlined"
          color="primary"
          size="large"
        >
          <label
            htmlFor={id}
            className="flex items-center justify-center cursor-pointer w-full px-5 py-2"
          >
            <Icon className="mr-2">upload</Icon>
            Upload Own Image
          </label>
        </Button>
        <input
          className="hidden"
          onChange={handleUpLoadImage}
          onClick={(event) => (event.target as HTMLInputElement).value = null}
          id={id}
          type="file"
          accept={allowPng ? 'image/png, image/jpeg' : 'image/jpeg'}
        />
      </div>
      <FormHelperText className="mt-2">
        Files supported: {allowPng ? 'png, jpeg, jpg' : 'jpeg, jpg'}. File size should be less than 250kB
        {recommendedSize && `. The size ${recommendedSize} is recommended.`}
      </FormHelperText>
      <GallerySelectOwnImage
        title={dialogTitle}
        imagesTagIds={ownImagesTagIds}
        isOwnImages={assetImagesData?.[ownImagesTagIds]?.items?.length > 0}
        onImageSelect={handleSelectImage}
        cols={3}
        rowHeight={rowHeight}
      />
      <BackdropCircular loading={uploadingPrivateImage} />
    </div>
  );
};

export default GalleryImagePicker;
